import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { AutomationGroup, AutomationLayout } from 'src/models/automation-group/_types';
import { reorderAutomationLayout } from 'src/models/automation-group/automation-group';
import { Identifier } from 'src/types/utilityTypes';

import { AutomationDragHandle } from './AutomationDragHandle';

const GROUP_CARD_SHADOW =
  '0 2px 5px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.06)';
const UNGROUPED_CARD_SHADOW =
  '0 2px 8px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.06)';
const DROP_ZONE_DIVIDER_HEIGHT = 4;
const DRAG_HIGHLIGHT_BG = 'primary.lightest';
const dragPlaceholderSx = {
  bgcolor: DRAG_HIGHLIGHT_BG,
  border: '1px dashed',
  borderColor: 'primary.main',
  borderRadius: 1,
};

const groupSortableId = (groupId: Identifier) => `group-${groupId}`;
const reminderSortableId = (reminderId: Identifier) => `reminder-${reminderId}`;
const ungroupedContainerId = 'container-ungrouped';
const groupContainerId = (groupId: Identifier) => `container-group-${groupId}`;

const normalizeId = (value: string): Identifier => {
  const numericId = Number(value);
  return Number.isNaN(numericId) ? value : numericId;
};

const idsMatch = (left: Identifier, right: Identifier) => String(left) === String(right);

const parseSortableId = (id: string) => {
  if (id.startsWith('group-')) {
    return { type: 'group' as const, id: normalizeId(id.replace('group-', '')) };
  }
  if (id.startsWith('reminder-')) {
    return { type: 'reminder' as const, id: normalizeId(id.replace('reminder-', '')) };
  }
  return null;
};

const getGroupIdFromContainer = (containerId: string): Identifier | null => {
  if (!containerId.startsWith('container-group-')) {
    return null;
  }

  return normalizeId(containerId.replace('container-group-', ''));
};

const resolveGroupDropTarget = (
  overId: string,
  reminderIdsByContainer: Record<string, Identifier[]>
): Identifier | null => {
  const overParsed = parseSortableId(overId);
  if (overParsed?.type === 'group') {
    return overParsed.id;
  }

  if (overParsed?.type === 'reminder') {
    const container = Object.keys(reminderIdsByContainer).find((containerKey) =>
      reminderIdsByContainer[containerKey].some((reminderId) =>
        idsMatch(reminderId, overParsed.id)
      )
    );

    return container ? getGroupIdFromContainer(container) : null;
  }

  return null;
};

const buildReminderContainerState = (layout: AutomationLayout) => {
  const reminderIdsByContainer: Record<string, Identifier[]> = {
    [ungroupedContainerId]: layout.ungroupedReminders
      .map((reminder) => reminder.id!)
      .filter(Boolean),
  };

  layout.automationGroups.forEach((group) => {
    reminderIdsByContainer[groupContainerId(group.id)] = (group.reminders || [])
      .map((reminder) => reminder.id!)
      .filter(Boolean);
  });

  return reminderIdsByContainer;
};

const getEmptyGroupKeys = (layout: AutomationLayout) =>
  new Set(
    layout.automationGroups
      .filter((group) => !(group.reminders || []).length)
      .map((group) => String(group.id))
  );

const buildLayoutFromState = (
  layout: AutomationLayout,
  groupOrder: Identifier[],
  reminderIdsByContainer: Record<string, Identifier[]>
): AutomationLayout => {
  const remindersById = new Map<string, AutomationLayout['ungroupedReminders'][number]>();
  layout.automationGroups.forEach((group) => {
    (group.reminders || []).forEach((reminder) => {
      if (reminder.id) {
        remindersById.set(String(reminder.id), reminder);
      }
    });
  });
  layout.ungroupedReminders.forEach((reminder) => {
    if (reminder.id) {
      remindersById.set(String(reminder.id), reminder);
    }
  });

  const groupsById = layout.automationGroups.reduce<Record<string, AutomationGroup>>(
    (acc, group) => {
      acc[String(group.id)] = group;
      return acc;
    },
    {}
  );

  return {
    automationGroups: groupOrder
      .map((groupId, position) => {
        const group = groupsById[String(groupId)];
        if (!group) {
          return null;
        }

        const reminderIds = reminderIdsByContainer[groupContainerId(groupId)] || [];
        return {
          ...group,
          position,
          reminders: reminderIds.map((reminderId, reminderPosition) => {
            const reminder = remindersById.get(String(reminderId));
            if (!reminder) {
              return null;
            }

            return {
              ...reminder,
              automationGroupId: groupId,
              position: reminderPosition,
            };
          }).filter(Boolean) as AutomationLayout['ungroupedReminders'],
        };
      })
      .filter(Boolean) as AutomationGroup[],
    ungroupedReminders: (reminderIdsByContainer[ungroupedContainerId] || [])
      .map((reminderId, position) => {
        const reminder = remindersById.get(String(reminderId));
        if (!reminder) {
          return null;
        }

        return {
          ...reminder,
          automationGroupId: null,
          position,
        };
      })
      .filter(Boolean) as AutomationLayout['ungroupedReminders'],
  };
};

interface SortableGroupSectionProps {
  children: ReactNode;
  collapsed: boolean;
  disabled?: boolean;
  groupId: Identifier;
  showHeaderDropDivider?: boolean;
  reminders: ReactNode;
}

const SortableGroupSection: FC<SortableGroupSectionProps> = ({
  children,
  collapsed,
  disabled = false,
  groupId,
  showHeaderDropDivider = false,
  reminders,
}) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ disabled, id: groupSortableId(groupId) });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      sx={[
        {
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
        isDragging
          ? dragPlaceholderSx
          : {
              bgcolor: collapsed ? 'background.paper' : 'background.level2',
              boxShadow: GROUP_CARD_SHADOW,
            },
      ]}
      {...attributes}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: collapsed ? 'background.paper' : 'background.level2',
          borderBottom: collapsed || showHeaderDropDivider ? 0 : 1,
          borderColor: 'neutral.6',
          display: 'flex',
          flex: 1,
          gap: 1,
          m: 0,
          pr: 2,
          pl: 1,
          py: 1.5,
        }}
      >
        <Box {...(disabled ? {} : listeners)}>
          <AutomationDragHandle
            active={isDragging}
            disabled={disabled}
            label={t('automations.groups.dragGroup')}
          />
        </Box>
        <Box flex={1}>{children}</Box>
      </Box>
      {showHeaderDropDivider ? (
        <Box bgcolor="primary.main" height={DROP_ZONE_DIVIDER_HEIGHT} width="100%" />
      ) : null}
      {!collapsed ? (
        <Box
          sx={{
            bgcolor: 'background.level2',
            display: 'flex',
            flexDirection: 'column',
            p: '1px',
          }}
        >
          {reminders}
        </Box>
      ) : null}
    </Box>
  );
};

interface SortableReminderRowProps {
  children: (dragHandle: ReactNode) => ReactNode;
  disabled?: boolean;
  reminderId: Identifier;
  variant?: 'grouped' | 'ungrouped';
}

const SortableReminderRow: FC<SortableReminderRowProps> = ({
  children,
  disabled = false,
  reminderId,
  variant = 'grouped',
}) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ disabled, id: reminderSortableId(reminderId) });

  const dragHandle = (
    <Box {...(disabled ? {} : listeners)}>
      <AutomationDragHandle
        active={isDragging}
        disabled={disabled}
        label={t('automations.groups.dragAutomation')}
      />
    </Box>
  );

  if (variant === 'grouped') {
    return (
      <Box
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}
        sx={[
          {
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'background.level2',
            m: 0,
            p: 0,
            pr: 2,
          },
          isDragging ? dragPlaceholderSx : {},
        ]}
        {...attributes}
      >
        {children(dragHandle)}
      </Box>
    );
  }

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      sx={[
        {
          borderRadius: 1,
          display: 'flex',
          overflow: 'hidden',
        },
        isDragging
          ? dragPlaceholderSx
          : {
              bgcolor: 'background.paper',
              boxShadow: UNGROUPED_CARD_SHADOW,
            },
      ]}
      {...attributes}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: '4px',
        }}
      />
      <Box flex={1} sx={{ py: 1, pr: 1 }}>
        {children(dragHandle)}
      </Box>
    </Box>
  );
};

interface RenderReminderFn {
  (
    reminderId: Identifier,
    dragHandle: ReactNode,
    options?: { dragging?: boolean; grouped?: boolean }
  ): ReactNode;
}

interface UngroupedRemindersSectionProps {
  disabled?: boolean;
  draggingFromGroup: boolean;
  hasGroups: boolean;
  reminderIds: Identifier[];
  renderReminder: RenderReminderFn;
}

const UngroupedRemindersSection: FC<UngroupedRemindersSectionProps> = ({
  disabled = false,
  draggingFromGroup,
  hasGroups,
  reminderIds,
  renderReminder,
}) => {
  const { t } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({
    disabled,
    id: ungroupedContainerId,
  });

  const showSection = reminderIds.length > 0 || hasGroups || draggingFromGroup;
  if (!showSection) {
    return null;
  }

  return (
    <Box ref={setNodeRef}>
      {reminderIds.length === 0 ? (
        <Box
          sx={[
            {
              alignItems: 'center',
              border: 1,
              borderColor: 'neutral.6',
              borderRadius: 1,
              borderStyle: 'dashed',
              display: 'flex',
              justifyContent: 'center',
              minHeight: 72,
              px: 2,
              py: 3,
            },
            isOver || draggingFromGroup
              ? {
                  bgcolor: DRAG_HIGHLIGHT_BG,
                  borderColor: 'primary.main',
                }
              : {},
          ]}
        >
          <Typography color="text.secondary" variant="body2">
            {t('automations.groups.ungrouped')}
          </Typography>
        </Box>
      ) : (
        <SortableContext
          items={reminderIds.map((reminderId) => reminderSortableId(reminderId))}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={2}>
            {reminderIds.map((reminderId) => (
              <SortableReminderRow
                disabled={disabled}
                key={reminderId}
                reminderId={reminderId}
                variant="ungrouped"
              >
                {(dragHandle) => renderReminder(reminderId, dragHandle)}
              </SortableReminderRow>
            ))}
          </Stack>
        </SortableContext>
      )}
    </Box>
  );
};

export interface AutomationGroupHeaderRenderProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

interface AutomationLayoutDnDProps {
  layout: AutomationLayout;
  onReordered: (layout: AutomationLayout) => void;
  onReorderFailed: () => void;
  renderGroupHeader: (
    group: AutomationGroup,
    headerProps: AutomationGroupHeaderRenderProps
  ) => ReactNode;
  renderReminder: RenderReminderFn;
}

export const AutomationLayoutDnD: FC<AutomationLayoutDnDProps> = ({
  layout,
  onReordered,
  onReorderFailed,
  renderGroupHeader,
  renderReminder,
}) => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dragOverlayWidth, setDragOverlayWidth] = useState<number | null>(null);
  const [isPersisting, setIsPersisting] = useState(false);
  const [groupOrder, setGroupOrder] = useState<Identifier[]>(
    layout.automationGroups.map((group) => group.id)
  );
  const [reminderIdsByContainer, setReminderIdsByContainer] = useState<
    Record<string, Identifier[]>
  >(() => buildReminderContainerState(layout));
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() =>
    getEmptyGroupKeys(layout)
  );
  const previousLayoutGroupIdsRef = useRef<Set<string>>(
    new Set(layout.automationGroups.map((group) => String(group.id)))
  );

  useEffect(() => {
    if (isPersisting) {
      return;
    }

    setGroupOrder(layout.automationGroups.map((group) => group.id));
    setReminderIdsByContainer(buildReminderContainerState(layout));
    setCollapsedGroups((previous) => {
      const previousGroupIds = previousLayoutGroupIdsRef.current;
      const next = new Set<string>();

      layout.automationGroups.forEach((group) => {
        const groupKey = String(group.id);
        const isEmpty = !(group.reminders || []).length;
        const isNew = !previousGroupIds.has(groupKey);

        if (previous.has(groupKey)) {
          next.add(groupKey);
        } else if (isNew && isEmpty) {
          next.add(groupKey);
        }
      });

      return next;
    });
    previousLayoutGroupIdsRef.current = new Set(
      layout.automationGroups.map((group) => String(group.id))
    );
  }, [isPersisting, layout]);

  const groupsById = layout.automationGroups.reduce<Record<string, AutomationGroup>>(
    (acc, group) => {
      acc[String(group.id)] = group;
      return acc;
    },
    {}
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const collisionDetection = (args: Parameters<typeof closestCenter>[0]) => {
    const activeParsed = parseSortableId(String(args.active.id));

    if (activeParsed?.type === 'group') {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((container) =>
          String(container.id).startsWith('group-')
        ),
      });
    }

    return closestCenter(args);
  };

  const toggleGroupCollapsed = (groupId: Identifier) => {
    const groupKey = String(groupId);
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const clearDragState = () => {
    setActiveId(null);
    setDragOverlayWidth(null);
  };

  const getReminderContainer = (reminderId: Identifier) =>
    Object.keys(reminderIdsByContainer).find((container) =>
      reminderIdsByContainer[container].some((id) => idsMatch(id, reminderId))
    );

  const activeParsed = activeId ? parseSortableId(activeId) : null;
  const draggingReminderId =
    activeParsed?.type === 'reminder' ? activeParsed.id : null;
  const draggingReminderSourceContainer = draggingReminderId
    ? getReminderContainer(draggingReminderId)
    : null;

  const isGroupEligibleForDrop = (groupId: Identifier) => {
    if (!draggingReminderId) {
      return false;
    }

    return draggingReminderSourceContainer !== groupContainerId(groupId);
  };

  const isDraggingFromGroup =
    draggingReminderSourceContainer != null &&
    draggingReminderSourceContainer !== ungroupedContainerId;

  const handleDragStart = (event: DragStartEvent) => {
    if (isPersisting) {
      return;
    }

    const id = String(event.active.id);
    setActiveId(id);
    setDragOverlayWidth(event.active.rect.current?.initial?.width ?? null);

    const parsed = parseSortableId(id);
    if (parsed?.type === 'group') {
      setCollapsedGroups((previous) => new Set(previous).add(String(parsed.id)));
    }
  };

  const renderDragOverlay = () => {
    if (!activeId) {
      return null;
    }

    const parsed = parseSortableId(activeId);
    if (!parsed) {
      return null;
    }

    if (parsed.type === 'group') {
      const group = groupsById[String(parsed.id)];
      if (!group) {
        return null;
      }

      return (
        <Box
          sx={{
            bgcolor: DRAG_HIGHLIGHT_BG,
            border: 1,
            borderColor: 'primary.main',
            borderRadius: 1,
            boxShadow: GROUP_CARD_SHADOW,
            cursor: 'grabbing',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: DRAG_HIGHLIGHT_BG,
              display: 'flex',
              gap: 1,
              pl: 1,
              pr: 2,
              py: 1.5,
            }}
          >
            <AutomationDragHandle
              active
              label={t('automations.groups.dragGroup')}
            />
            <Box flex={1}>
              {renderGroupHeader(group, {
                collapsed: true,
                onToggleCollapse: () => undefined,
              })}
            </Box>
          </Box>
        </Box>
      );
    }

    const dragHandle = (
      <AutomationDragHandle
        active
        label={t('automations.groups.dragAutomation')}
      />
    );

    const isGrouped = draggingReminderSourceContainer !== ungroupedContainerId;

    if (isGrouped) {
      return (
        <Box
          sx={{
            bgcolor: DRAG_HIGHLIGHT_BG,
            border: 1,
            borderColor: 'primary.main',
            borderRadius: 1,
            cursor: 'grabbing',
            overflow: 'hidden',
            pr: 2,
          }}
        >
          {renderReminder(parsed.id, dragHandle, {
            dragging: true,
            grouped: true,
          })}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          bgcolor: DRAG_HIGHLIGHT_BG,
          border: 1,
          borderColor: 'primary.main',
          borderRadius: 1,
          boxShadow: UNGROUPED_CARD_SHADOW,
          cursor: 'grabbing',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <Box flexShrink={0} width="4px" />
        <Box flex={1} sx={{ py: 1, pr: 1 }}>
          {renderReminder(parsed.id, dragHandle, {
            dragging: true,
            grouped: false,
          })}
        </Box>
      </Box>
    );
  };

  const persistLayout = async (
    previousGroupOrder: Identifier[],
    previousReminderIdsByContainer: Record<string, Identifier[]>,
    nextGroupOrder: Identifier[],
    nextReminderIdsByContainer: Record<string, Identifier[]>
  ) => {
    setIsPersisting(true);
    try {
      await reorderAutomationLayout({
        groups: nextGroupOrder.map((groupId, position) => ({
          id: groupId,
          position,
          reminderIds: nextReminderIdsByContainer[groupContainerId(groupId)] || [],
        })),
        ungroupedReminderIds: nextReminderIdsByContainer[ungroupedContainerId] || [],
      });
      onReordered(buildLayoutFromState(layout, nextGroupOrder, nextReminderIdsByContainer));
    } catch {
      setGroupOrder(previousGroupOrder);
      setReminderIdsByContainer(previousReminderIdsByContainer);
      onReorderFailed();
    } finally {
      setIsPersisting(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    clearDragState();
    if (isPersisting || !over || active.id === over.id) {
      return;
    }

    const overId = String(over.id);
    const activeParsed = parseSortableId(String(active.id));
    if (!activeParsed) {
      return;
    }

    if (activeParsed.type === 'group') {
      const overGroupId = resolveGroupDropTarget(overId, reminderIdsByContainer);
      if (!overGroupId) {
        return;
      }

      const oldIndex = groupOrder.findIndex(
        (id) => groupSortableId(id) === String(active.id)
      );
      const newIndex = groupOrder.findIndex(
        (id) => String(id) === String(overGroupId)
      );
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      const previousGroupOrder = groupOrder;
      const nextGroupOrder = arrayMove(groupOrder, oldIndex, newIndex);
      setGroupOrder(nextGroupOrder);
      await persistLayout(
        previousGroupOrder,
        reminderIdsByContainer,
        nextGroupOrder,
        reminderIdsByContainer
      );
      return;
    }

    if (activeParsed.type !== 'reminder') {
      return;
    }

    const overParsed = parseSortableId(overId);

    const sourceContainer = Object.keys(reminderIdsByContainer).find((container) =>
      reminderIdsByContainer[container].some((reminderId) =>
        idsMatch(reminderId, activeParsed.id)
      )
    );
    if (!sourceContainer) {
      return;
    }

    let targetContainer = sourceContainer;
    if (overId === ungroupedContainerId) {
      targetContainer = ungroupedContainerId;
    } else if (overParsed?.type === 'group') {
      targetContainer = groupContainerId(overParsed.id);
    } else if (overParsed?.type === 'reminder') {
      targetContainer =
        Object.keys(reminderIdsByContainer).find((container) =>
          reminderIdsByContainer[container].some((reminderId) =>
            idsMatch(reminderId, overParsed.id)
          )
        ) || sourceContainer;
    } else {
      return;
    }

    const previousReminderIdsByContainer = reminderIdsByContainer;
    let nextReminderIdsByContainer: Record<string, Identifier[]>;

    if (sourceContainer === targetContainer && overParsed?.type === 'reminder') {
      const containerIds = [...reminderIdsByContainer[sourceContainer]];
      const oldIndex = containerIds.findIndex((reminderId) =>
        idsMatch(reminderId, activeParsed.id)
      );
      const newIndex = containerIds.findIndex((reminderId) =>
        idsMatch(reminderId, overParsed.id)
      );
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      nextReminderIdsByContainer = {
        ...reminderIdsByContainer,
        [sourceContainer]: arrayMove(containerIds, oldIndex, newIndex),
      };
    } else {
      const sourceIds = [...reminderIdsByContainer[sourceContainer]];
      const sourceIndex = sourceIds.findIndex((reminderId) =>
        idsMatch(reminderId, activeParsed.id)
      );
      if (sourceIndex === -1) {
        return;
      }

      sourceIds.splice(sourceIndex, 1);
      const targetIds = [...(reminderIdsByContainer[targetContainer] || [])];

      let targetIndex = targetIds.length;
      if (overParsed?.type === 'reminder') {
        const overIndex = targetIds.findIndex((reminderId) =>
          idsMatch(reminderId, overParsed.id)
        );
        if (overIndex !== -1) {
          targetIndex = overIndex;
        }
      }

      targetIds.splice(targetIndex, 0, activeParsed.id);

      nextReminderIdsByContainer = {
        ...reminderIdsByContainer,
        [sourceContainer]: sourceIds,
        [targetContainer]: targetIds,
      };
    }

    setReminderIdsByContainer(nextReminderIdsByContainer);
    await persistLayout(
      groupOrder,
      previousReminderIdsByContainer,
      groupOrder,
      nextReminderIdsByContainer
    );
  };

  const ungroupedReminderIds = reminderIdsByContainer[ungroupedContainerId] || [];

  return (
    <DndContext
      collisionDetection={collisionDetection}
      onDragCancel={clearDragState}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={isPersisting ? [] : sensors}
    >
      <Stack spacing={2}>
        <SortableContext
          items={groupOrder.map((groupId) => groupSortableId(groupId))}
          strategy={verticalListSortingStrategy}
        >
          {groupOrder.map((groupId) => {
            const group = groupsById[String(groupId)];
            if (!group) {
              return null;
            }

            const containerKey = groupContainerId(groupId);
            const reminderIds = reminderIdsByContainer[containerKey] || [];
            const collapsed = collapsedGroups.has(String(groupId));

            return (
              <SortableGroupSection
                collapsed={collapsed}
                disabled={isPersisting}
                groupId={groupId}
                key={groupId}
                showHeaderDropDivider={isGroupEligibleForDrop(groupId)}
                reminders={
                  <SortableContext
                    items={reminderIds.map((reminderId) => reminderSortableId(reminderId))}
                    strategy={verticalListSortingStrategy}
                  >
                    <Box sx={{ m: 0, p: 0 }}>
                      {reminderIds.map((reminderId) => (
                        <SortableReminderRow
                          disabled={isPersisting}
                          key={reminderId}
                          reminderId={reminderId}
                          variant="grouped"
                        >
                          {(dragHandle) =>
                            renderReminder(reminderId, dragHandle, { grouped: true })
                          }
                        </SortableReminderRow>
                      ))}
                    </Box>
                  </SortableContext>
                }
              >
                {renderGroupHeader(group, {
                  collapsed,
                  onToggleCollapse: () => toggleGroupCollapsed(groupId),
                })}
              </SortableGroupSection>
            );
          })}
        </SortableContext>

        <UngroupedRemindersSection
          disabled={isPersisting}
          draggingFromGroup={isDraggingFromGroup}
          hasGroups={layout.automationGroups.length > 0}
          reminderIds={ungroupedReminderIds}
          renderReminder={renderReminder}
        />
      </Stack>

      <DragOverlay>
        {activeId ? (
          <Box sx={{ width: dragOverlayWidth ?? undefined }}>{renderDragOverlay()}</Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
